import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Save, Trash2, Plus } from "lucide-react";

/**
 * RegionSlotsPage — Admin controller for founding-cohort scarcity signal.
 * Set slots_taken / slots_total per region (e.g. Maldives 4/7).
 * Value surfaces on the public proposal page's Founding Advantage modal.
 */
export default function RegionSlotsPage() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ region: "", slots_taken: 0, slots_total: 7 });
  const [saving, setSaving] = useState(false);

  const authCfg = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/region-slots`, authCfg);
      setRows(res.data || []);
    } catch (e) {
      toast.error("Failed to load region slots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const upsert = async (payload) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/region-slots`, payload, authCfg);
      toast.success(`${payload.region} saved`);
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!draft.region.trim()) { toast.error("Region is required"); return; }
    const ok = await upsert({
      region: draft.region.trim(),
      slots_taken: Number(draft.slots_taken) || 0,
      slots_total: Number(draft.slots_total) || 7,
    });
    if (ok) setDraft({ region: "", slots_taken: 0, slots_total: 7 });
  };

  const handleInlineUpdate = async (row, field, value) => {
    const updated = { ...row, [field]: Number(value) };
    await upsert({
      region: row.region,
      slots_taken: updated.slots_taken,
      slots_total: updated.slots_total,
    });
  };

  const handleDelete = async (region_key) => {
    if (!window.confirm("Delete this region?")) return;
    try {
      await axios.delete(`${API}/admin/region-slots/${encodeURIComponent(region_key)}`, authCfg);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="region-slots-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <MapPin className="w-5 h-5 text-[#C9A96E]" />
          <h1 className="text-2xl font-light text-white">Region Slots</h1>
        </div>
        <p className="text-white/50 text-sm max-w-2xl">
          Controls the founding-cohort scarcity signal on public proposals. Set how many slots are already secured per region (e.g. <span className="text-[#C9A96E]">Maldives 4 / 7</span>). The value appears under the validity timer in the Founding Advantage modal.
        </p>
      </div>

      {/* Add new region */}
      <div className="mb-8 p-5 rounded-xl border border-white/10 bg-white/[0.02]" data-testid="region-slots-add-card">
        <p className="text-[#C9A96E]/70 text-[10px] tracking-[0.28em] uppercase font-bold mb-4">Add / Override Region</p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_120px_auto] gap-3 items-end">
          <div>
            <label className="text-white/50 text-xs mb-1 block">Region</label>
            <input
              value={draft.region}
              onChange={(e) => setDraft({ ...draft, region: e.target.value })}
              placeholder="e.g. Maldives"
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-[#C9A96E]/40"
              data-testid="region-input"
            />
          </div>
          <div>
            <label className="text-white/50 text-xs mb-1 block">Taken</label>
            <input
              type="number"
              min="0"
              max="50"
              value={draft.slots_taken}
              onChange={(e) => setDraft({ ...draft, slots_taken: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-[#C9A96E]/40 tabular-nums"
              data-testid="slots-taken-input"
            />
          </div>
          <div>
            <label className="text-white/50 text-xs mb-1 block">Total</label>
            <input
              type="number"
              min="1"
              max="50"
              value={draft.slots_total}
              onChange={(e) => setDraft({ ...draft, slots_total: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-[#C9A96E]/40 tabular-nums"
              data-testid="slots-total-input"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-[#C9A96E] text-black font-semibold text-sm hover:bg-[#D4B87A] transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="region-slots-save-btn"
          >
            <Plus className="w-4 h-4" /> Save
          </button>
        </div>
      </div>

      {/* Existing rows */}
      {loading ? (
        <p className="text-white/40 text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-white/40 text-sm italic">No regions configured yet. Add one above.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const pct = row.slots_total > 0 ? Math.min(100, (row.slots_taken / row.slots_total) * 100) : 0;
            const remaining = Math.max(0, row.slots_total - row.slots_taken);
            return (
              <div
                key={row.region_key}
                className="p-5 rounded-xl border border-white/10 bg-white/[0.02] flex items-center gap-6"
                data-testid={`region-row-${row.region_key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 mb-2">
                    <h3 className="text-white font-semibold text-base">{row.region}</h3>
                    <p className="text-white/30 text-[10px] tracking-widest uppercase">{remaining} remaining</p>
                  </div>
                  {/* Visual bar */}
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #C9A96E, #E8C872)' }} />
                  </div>
                  <p className="text-white/30 text-[10px] mt-2">Updated {new Date(row.updated_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    type="number"
                    min="0"
                    max={row.slots_total}
                    defaultValue={row.slots_taken}
                    onBlur={(e) => { if (Number(e.target.value) !== row.slots_taken) handleInlineUpdate(row, 'slots_taken', e.target.value); }}
                    className="w-20 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-[#C9A96E]/40 tabular-nums text-center"
                    data-testid={`inline-taken-${row.region_key}`}
                  />
                  <span className="text-white/30 text-sm">/</span>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    defaultValue={row.slots_total}
                    onBlur={(e) => { if (Number(e.target.value) !== row.slots_total) handleInlineUpdate(row, 'slots_total', e.target.value); }}
                    className="w-20 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-[#C9A96E]/40 tabular-nums text-center"
                    data-testid={`inline-total-${row.region_key}`}
                  />
                  <button
                    onClick={() => handleDelete(row.region_key)}
                    className="p-2 rounded-lg hover:bg-red-500/10 text-white/40 hover:text-red-400 transition-colors"
                    data-testid={`delete-${row.region_key}`}
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
